// eslint-disable-next-line no-undef
const isTestSSP = process.env.MIX_TEST_SSP === 'true';
const webId = process.env.MIX_WEB_ID;

const BASE_URL = '/api/adds';
const BRANDING = 'branding';
const MOBILE_SCREEN_BREAKPOINT = 821;
const MAX_FILE_SIZE = 500 * 1024; // 300 kB
const MOBILE = 'mobile';
const DESKTOP = 'desktop';

export default () => ({
  adds: [],
  sssp: null,
  webContainer: null,
  mobileBranding: document.getElementById('mobile-branding'),

  get isMobile() {
    return window.innerWidth < MOBILE_SCREEN_BREAKPOINT;
  },

  init() {
    this.webContainer = document.getElementById('web-container');
    this.handleAdPreview(); // Handle preview mode

    const urlParams = new URLSearchParams(window.location.search);

    this.initSsp();
  },

  initSsp() {
    // With `defer` on both the SSP script and site.js, the SSP `load` event fires
    // before Alpine initialises this component — so we check synchronously first.
    if (window.sssp !== undefined) {
      this.handleSspLoad();
    } else {
      const sspScript = document.getElementById('ssp-script');
      sspScript.addEventListener('load', this.handleSspLoad.bind(this));
    }
  },

  async removeBranding() {
    this.webContainer.classList.remove('has-branding');

    // Select all elements with data-add="branding"
    const brandingDivsToRemove = document.querySelectorAll(`div[data-add="branding"]`);

    // Also remove mobile branding placeholder divs
    const mobilePlaceholderDivs = document.querySelectorAll(`div.adds--branding-mobile-placeholder`);

    // Create a promise for each element to be removed
    await Promise.all(Array.from(brandingDivsToRemove).map(div => {
      return new Promise((resolve) => {
        div.remove();
        resolve();
      });
    }));

    // Remove mobile placeholder divs
    await Promise.all(Array.from(mobilePlaceholderDivs).map(div => {
      return new Promise((resolve) => {
        div.remove();
        resolve();
      });
    }));
  },

  handleSeznamAddTags() {
    return new Promise((resolve) => {
      const placementToRemove = this.isMobile ? 'desktop' : 'mobile';

      // Select and remove all divs with the specific placement
      const divsToRemove = document.querySelectorAll(`div[data-placement="${placementToRemove}"]`);
      divsToRemove.forEach(div => div.remove());

      resolve();
    });
  },

  shuffleArray(items) {
    const array = [...items];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  },

  getOwnBannersPercent() {
    const raw = typeof window !== 'undefined' && typeof window.__GV_OWN_BANNERS_PERCENT !== 'undefined'
      ? Number(window.__GV_OWN_BANNERS_PERCENT)
      : 100;
    if (Number.isNaN(raw)) {
      return 100;
    }
    return Math.min(100, Math.max(0, raw));
  },

  distributeAdsByMix(ownPercent) {
    const addsByType = this.adds.reduce((acc, add) => {
      if (!acc[add.type]) {
        acc[add.type] = [];
      }
      acc[add.type].push(add);
      return acc;
    }, {});

    Object.keys(addsByType).forEach((type) => {
      addsByType[type] = this.shuffleArray(addsByType[type]);
    });

    const placement = this.isMobile ? 'mobile' : 'desktop';
    const slots = Array.from(
      document.querySelectorAll(`[data-add][data-placement="${placement}"]`)
    ).filter((el) => (
      !el.classList.contains('adds--preview')
    ));
    const mixableSlots = this.shuffleArray(
      slots.filter(el => el.hasAttribute('data-ssp-mixable'))
    );
    const nonMixableSlots = this.shuffleArray(
      slots.filter(el => !el.hasAttribute('data-ssp-mixable'))
    );

    const targetOwnOnMixable = Math.min(
      mixableSlots.length,
      Math.max(0, Math.round((mixableSlots.length * ownPercent) / 100))
    );
    let ownAssignedOnMixable = 0;

    mixableSlots.forEach((element) => {
      if (ownAssignedOnMixable >= targetOwnOnMixable) {
        return;
      }

      const addType = element.getAttribute('data-add');
      const addInstances = addType ? addsByType[addType] : null;
      if (!addInstances || addInstances.length === 0) {
        return;
      }

      while (addInstances.length > 0) {
        const candidate = addInstances[0];
        if (this.renderAd(element, candidate)) {
          addInstances.shift();
          ownAssignedOnMixable += 1;
          break;
        }
        addInstances.shift();
      }
    });

    nonMixableSlots.forEach((element) => {
      const addType = element.getAttribute('data-add');
      const addInstances = addType ? addsByType[addType] : null;
      if (!addInstances || addInstances.length === 0) {
        return;
      }

      while (addInstances.length > 0) {
        const candidate = addInstances[0];
        if (this.renderAd(element, candidate)) {
          addInstances.shift();
          break;
        }
        addInstances.shift();
      }
    });
  },

  renderAd(element, add) {
    // Skip elements that have the "adds--preview" class (preview mode)
    if (element.classList.contains('adds--preview')) {
      return false;
    }

    const isBranding = add.type === BRANDING;

    // Choose the image based on the screen width
    let imageUrl = this.isMobile && add.banner.mobile ? add.banner.mobile : add.banner.desktop;
    let targetUrl = add.target;

    // For branding banners, if the required image type is null, try to find a replacement
    if (isBranding && !imageUrl) {
      const replacement = this.findBrandingReplacement(this.isMobile ? 'mobile' : 'desktop');
      if (replacement) {
        imageUrl = replacement.banner[this.isMobile ? 'mobile' : 'desktop'];
        targetUrl = replacement.target;
      } else {
        // No replacement found, don't render the image to avoid showing alt text
        return false;
      }
    }

    // If still no image URL, don't render
    if (!imageUrl) {
      return false;
    }

    const banner = document.createElement('a');
    banner.href = targetUrl;
    banner.target = '_blank'; // Open in new tab/window

    if (isBranding && this.isMobile) {
      element.classList.add('adds--branding-mobile');
    }

    const image = document.createElement('img');

    image.src = imageUrl;
    image.alt = `${add.type} banner`;
    banner.appendChild(image);
    element.appendChild(banner); // Append the banner link to the element

    // Remove the data-szn-ssp-ad attribute
    element.removeAttribute('data-szn-ssp-ad');
    element.removeAttribute('data-ssp-mixable');
    return true;
  },

  findBrandingReplacement(requiredType) {
    // Find all branding banners that have the required image type
    const brandingBanners = this.adds.filter(add =>
      add.type === BRANDING &&
      add.banner[requiredType] !== null
    );

    // Return the first available branding banner with the required image type
    return brandingBanners.length > 0 ? brandingBanners[0] : null;
  },

  removeEmptyElements() {
    document.querySelectorAll('[data-add]').forEach((element) => {
      if (element.hasAttribute('data-szn-ssp-ad')) return;
      setTimeout(() => {
        if (!element.hasChildNodes() || !element.innerHTML.trim()) {
          const parent = element.closest('.adds__popup-mobile-wrapper');
          if (parent) {
            parent.remove();
          } else {
            element.remove();
          }
        }
      }, 300);
    });
  },

  removeEmptySspElements() {
    document.querySelectorAll('[data-szn-ssp-ad]').forEach((element) => {
      if (!element.hasChildNodes() || !element.innerHTML.trim()) {
        const parent = element.closest('.adds__popup-mobile-wrapper');
        if (parent) {
          parent.remove();
        } else {
          element.remove();
        }
      }
    });
  },

  checkForBranding() {
    const hasBranding = this.adds.some((add) => add.type === BRANDING);
    if (!hasBranding) {
      this.removeBranding();
    }
  },

  async fetchCustomAdds() {
    this.loading = true;
    try {
      // Získání slugu z cesty URL
      const path = window.location.pathname;
      const slug = path.split('/').filter(Boolean).pop() || null;

      // Přidání slugu jako GET parametru
      const url = slug ? `${BASE_URL}?slug=${slug}`: BASE_URL;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      this.adds = (data && Array.isArray(data.data)) ? data.data : [];
    } catch (error) {
      console.error('There was a problem fetching the adds:', error);
    } finally {
      this.loading = false;
      this.distributeAdsByMix(this.getOwnBannersPercent());
      this.checkForBranding();
    }
  },

  async handleSspLoad() {
    this.sssp = window.sssp; // Bind window.sssp to this.sssp
    this.sssp.config({ webId: webId, test: isTestSSP });
    const seznamTraffic = this.sssp.displaySeznamAds();
    if (seznamTraffic) {
      await this.removeBranding();
    } else {
      await this.fetchCustomAdds();
    }
    await this.handleSeznamAddTags();
    // Always call getAdsByTags — distributeAdsByMix already removed data-szn-ssp-ad
    // from slots filled with own banners, so SSP only fills what's left.
    this.sssp.getAdsByTags();
    setTimeout(() => this.removeEmptyElements(), 300);
    setTimeout(() => this.removeEmptySspElements(), 5000);
  },

  handleAdPreview() {
    const previewAddType = this.getPreviewAddType();

    if (previewAddType) {
      const addElement = this.getAddElement(previewAddType);
      if (addElement) {
        this.setupPreviewElement(addElement);

        setTimeout(() => {
          addElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500); // Delay for the image to load
        // Scroll to the banner element
      }
    }
  },

  getPreviewAddType() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('adds-preview');
  },

  getAddElement(previewAddType) {
    // Check if the ad type is 'branding'
    if (previewAddType === BRANDING) {
      // Use 'data-placement' to differentiate between mobile and desktop
      const placement = this.isMobile ? MOBILE : DESKTOP;
      return document.querySelector(`[data-add="branding"][data-placement="${placement}"]`);
    }

    // Default behavior for non-branding ads
    return document.querySelector(`[data-add="${previewAddType}"]`);
  },


  setupPreviewElement(addElement) {
    const dimension = addElement.getAttribute('data-dimension');

    this.applyPreviewStyles(addElement, dimension);
    this.removeAdAttributes(addElement);
    this.createPreviewContent(addElement, dimension);
  },

  applyPreviewStyles(addElement, dimension) {
    addElement.classList.add('adds--preview');
    const [width, height] = dimension.split('x');
    addElement.style.width = `${width}px`;
    addElement.style.height = `${height}px`;
  },

  removeAdAttributes(addElement) {
    addElement.removeAttribute('data-szn-ssp-ad');
    addElement.removeAttribute('data-ssp-mixable');
    addElement.removeAttribute('id');
  },

  createFileInput() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg';
    fileInput.style.display = 'block';
    return fileInput;
  },

  createPreviewContent(addElement, dimension) {
    const previewDiv = document.createElement('div');
    previewDiv.innerHTML = `<p class="info-text">Rozměry: ${dimension}</p><p class="info-text">Maximální velikost: ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)} MB</p>`;

    const fileUploadWrapper = document.createElement('div');
    fileUploadWrapper.classList.add('file-upload');

    const fileInputLabel = document.createElement('label');
    fileInputLabel.classList.add('file-upload-label');
    fileInputLabel.textContent = 'Vybrat soubor';

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg';
    fileInput.classList.add('file-upload-input');

    const changeBannerBtn = document.createElement('button');
    changeBannerBtn.textContent = 'Změnit banner';
    changeBannerBtn.classList.add('change-banner-btn');

    fileUploadWrapper.appendChild(fileInputLabel);
    fileUploadWrapper.appendChild(fileInput);
    previewDiv.appendChild(fileUploadWrapper);
    previewDiv.appendChild(changeBannerBtn);

    addElement.innerHTML = ''; // Clear the existing content
    addElement.appendChild(previewDiv);

    this.handleFileUpload(fileInput, previewDiv, changeBannerBtn, previewDiv, addElement);
  },

  handleFileUpload(fileInput, previewDiv, changeBannerBtn, previewContainer, addElement) {
    // Listen for changes to the file input
    fileInput.addEventListener('change', () => {
      const file = fileInput.files[0];

      // Check file before uploading
      if (file && file.size <= MAX_FILE_SIZE && file.type === 'image/jpeg') {
        const reader = new FileReader();
        reader.onload = (e) => {
          // Remove any previous images if they exist
          previewContainer.querySelectorAll('img').forEach(img => img.remove());

          // Add the new image
          const image = new Image();
          image.src = e.target.result;

          // If branding, set image size based on dimensions
          if (addElement.classList.contains('adds--branding')) {
            const dimension = addElement.getAttribute('data-dimension');
            if (dimension) {
              const [width, height] = dimension.split('x');
              image.style.width = `${width}px`;
              image.style.height = `${height}px`;
            }
          } else {
            image.style.maxWidth = '100%';
          }

          previewDiv.appendChild(image);

          // Add class to the parent div (addElement) that holds data attributes
          addElement.classList.add('adds--preview-set');
        };
        reader.readAsDataURL(file);
      } else if (file) {
        // If file doesn't meet conditions, show an alert in Czech and reset the input
        alert('Soubor musí být ve formátu JPG a menší než ' + (MAX_FILE_SIZE / 1024 / 1024).toFixed(1) + ' MB');
        fileInput.value = ''; // Reset the file input
      }
    });

    // Event for the "Change banner" button
    changeBannerBtn.addEventListener('click', () => {
      // Remove the displayed image
      previewContainer.querySelectorAll('img').forEach(img => img.remove());

      // Remove class from the parent div (addElement) that holds data attributes
      addElement.classList.remove('adds--preview-set');
    });
  }
});
